import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { connect } from 'redux-bundler-react';
import Spinner from '@components/spinner/spinner';
import { Alert } from '@trussworks/react-uswds';
import { useQuery } from '@tanstack/react-query';
import { RIBITS_QUERY_KEYS, RibitsQueries } from '@src/api/services/ribits';

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const currentEnv = import.meta.env.VITE_ENVIRONMENT;
const useLegacyEndpoints = currentEnv === 'development' || currentEnv === 'demo';
const defaultBankILFOptions = [
  {
    label: 'Not Listed',
    value: 'Not Listed',
    id: 999999999,
  },
];
const unavailableOption = [
  {
    label: 'RIBITS Unavailable',
    value: 'RIBITS Unavailable',
    id: 999999991,
  },
];
const MITIGATION_TYPES = {
  MITIGATION_BANK: 'Mitigation Bank',
  IN_LIEU_FEE: 'In-lieu Fee',
};
const createUniqueOptions = (items) => {
  const uniqueNames = new Set();
  const options = [...defaultBankILFOptions];

  items?.forEach((item) => {
    const name = item.name;
    if (!uniqueNames.has(name)) {
      uniqueNames.add(name);
      options.push({
        value: name,
        label: name,
        id: item.id,
      });
    }
  });
  return options.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));
};

const BankILFTableCell = connect(
  'selectRequestFormData',
  'selectRequestFormDataLocation',
  ({ getValue, row, column, table, cell, requestFormData, requestFormDataLocation }) => {
    const columnMeta = column.columnDef.meta;
    const tableMeta = table.options.meta;
    const initialValue = getValue();
    const districtName = requestFormData?.location?.district?.replace(/ District$/i, '') || '';
    const { latitude, longitude } = requestFormDataLocation || {};

    const [value, setValue] = useState(initialValue);
    const [selectOptions, setSelectOptions] = useState([]);
    const [cellInView, setCellInView] = useState(false);

    const debouncedUpdateRef = useRef();
    const observerRef = useRef();
    const rowMitigationType = useMemo(() => row.getValue('mitigationType'), [row]);

    useEffect(() => {
      debouncedUpdateRef.current = debounce((newValue) => {
        if (tableMeta?.updateData) {
          tableMeta.updateData(row.index, column.id, newValue);
        }
      }, 500);
    }, [row.index, column.id, tableMeta?.updateData, columnMeta?.type, tableMeta]);

    const updateValue = useCallback((newValue) => {
      debouncedUpdateRef.current(newValue);
    }, []);

    const {
      data: bankSiteList,
      isError: isBankSiteError,
      isLoading: isLoadingBankSites,
    } = useQuery({
      queryKey: [RIBITS_QUERY_KEYS.GET_BANK_SITES, districtName],
      queryFn: () => RibitsQueries.getBankSiteList('district', districtName),
      enabled:
        cellInView &&
        useLegacyEndpoints &&
        rowMitigationType === MITIGATION_TYPES.MITIGATION_BANK &&
        districtName?.length > 0,
    });

    const {
      data: ilfProgramList,
      isError: isIlfProgramListError,
      isLoading: isLoadingIlfProgramList,
    } = useQuery({
      queryKey: [RIBITS_QUERY_KEYS.GET_ILF_PROGRAM_LIST, districtName],
      queryFn: () => RibitsQueries.getIlfProgramList('district', districtName),
      enabled:
        cellInView &&
        useLegacyEndpoints &&
        rowMitigationType === MITIGATION_TYPES.IN_LIEU_FEE &&
        districtName?.length > 0,
    });

    const {
      data: mitigationBanksOrIlf,
      isError: isMitigationBankOrIlfError,
      isLoading: isLoadingMitigationBanksOrIlf,
    } = useQuery({
      queryKey: [RIBITS_QUERY_KEYS.GET_MITIGATION_BANKS_OR_ILF, rowMitigationType, latitude, longitude],
      queryFn: () => RibitsQueries.getMitigationBanksOrIlf(rowMitigationType, latitude, longitude),
      enabled: !useLegacyEndpoints && rowMitigationType?.length > 0 && cellInView,
    });

    const isError = isBankSiteError || isIlfProgramListError || isMitigationBankOrIlfError;
    const isLoading = isLoadingBankSites || isLoadingIlfProgramList || isLoadingMitigationBanksOrIlf;

    const handleIntersection = useCallback(
      (entries) => {
        entries.forEach((entry) => {
          setCellInView(entry.isIntersecting);

          if (entry.isIntersecting) {
            if (observerRef.current) {
              observerRef.current.unobserve(entry.target);
            }
          }
        });
      },
      [observerRef]
    );

    const handleChange = (e) => {
      const val = e?.target?.value;
      setValue(val);
      updateValue(val);
      // Set bankOrILFNumber
      const mappedBankId = selectOptions?.filter((item) => item.label === val)?.[0]?.id;
      tableMeta?.updateData(row.index, 'bankOrILFNumber', mappedBankId);
    };

    // Set-up Intersection Observer to trigger queries when the cell is in view
    useEffect(() => {
      const observer = new IntersectionObserver(handleIntersection, {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      });
      observerRef.current = observer;
      const element = document.getElementById(cell.id);
      if (element) {
        observerRef.current.observe(element);
      }
      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, [cell.id, handleIntersection]);

    // Set options based on endpoint and row-mitigation-type settings
    useEffect(() => {
      if (isLoading) return;

      if (useLegacyEndpoints) {
        if (rowMitigationType === MITIGATION_TYPES.MITIGATION_BANK) {
          setSelectOptions(createUniqueOptions(bankSiteList));
        } else if (rowMitigationType === MITIGATION_TYPES.IN_LIEU_FEE) {
          setSelectOptions(createUniqueOptions(ilfProgramList));
        }
      } else {
        if (rowMitigationType) {
          setSelectOptions(createUniqueOptions(mitigationBanksOrIlf));
        } else {
          setSelectOptions(defaultBankILFOptions);
        }
      }
    }, [bankSiteList, ilfProgramList, isLoading, mitigationBanksOrIlf, rowMitigationType]);

    // Toggle error message based on query error states
    useEffect(() => {
      if (isError) {
        setSelectOptions(unavailableOption);
      }
    }, [isError]);

    return (
      <div
        id={cell.id}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isError && (
          <span>
            <Alert slim type='error'>
              RIBITS is unavailable at this time. Select "RIBITS Unavailable" in the Bank/ILF Name dropdown and provide
              any known Bank/ILF Name details in the Description of Avoidance, Minimization and Compensation field.
            </Alert>
          </span>
        )}
        {isLoading && !isError ? (
          <Spinner />
        ) : (
          <select
            aria-label={'Bank/ILF Name'}
            disabled={columnMeta?.readOnly}
            id={cell.id}
            onChange={handleChange}
            required={columnMeta?.required}
            style={{
              cursor: columnMeta?.readOnly ? 'not-allowed' : 'auto',
              borderColor: 'hsl(0, 0%, 80%)',
              marginTop: '4px',
              width: '100%',
            }}
            value={value ?? ''}
          >
            <option key={0} value='' className='none' style={{ display: 'none' }}>
              -- Select a value --
            </option>
            {selectOptions?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
      </div>
    );
  }
);
export default BankILFTableCell;
